import { useState, useEffect } from "react";

/**
 * Modal per il motivo obbligatorio di una correzione (DDT — Casadei).
 *
 * Ogni modifica a un campo (header o riga) deve essere accompagnata da un
 * motivo prima di poter essere salvata in `corrections`. Il salvataggio del
 * singolo campo resta bloccato finché l'operatore non conferma qui.
 */
export const CORRECTION_REASON_OPTIONS = [
  "Codice errato",
  "Descrizione incompleta",
  "Prezzo/sconto errato",
  "Match articolo errato",
  "Altro",
] as const;

export type CorrectionReasonOption = (typeof CORRECTION_REASON_OPTIONS)[number];

interface CorrectionReasonModalProps {
  fieldLabel: string;
  oldValue: string;
  newValue: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function CorrectionReasonModal({
  fieldLabel,
  oldValue,
  newValue,
  onConfirm,
  onCancel,
}: CorrectionReasonModalProps) {
  const [reason, setReason] = useState<CorrectionReasonOption>("Codice errato");
  const [freeText, setFreeText] = useState("");

  // Chiudi con ESC (senza salvare) — annulla e ripristina il valore precedente.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const needsFreeText = reason === "Altro";
  const canConfirm = !needsFreeText || freeText.trim().length > 0;

  function handleConfirm() {
    if (!canConfirm) return;
    onConfirm(needsFreeText ? freeText.trim() : reason);
  }

  return (
    <div
      // Guardia mousedown+click: click sul backdrop non chiude accidentalmente
      // se il mousedown è partito dentro il pannello (pattern da altri modal FE).
      onMouseDown={(e) => { if (e.target === e.currentTarget) (e.currentTarget as any)._downOnBackdrop = true; }}
      onClick={(e) => { if (e.target === e.currentTarget && (e.currentTarget as any)._downOnBackdrop) onCancel(); }}
      className="dialog-overlay"
    >
      <div className="dialog-card" style={{ maxWidth: 420 }}>
        <div className="dialog-header">
          <h2 style={{ fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-warning)" }}>edit_note</span>
            Motivo della correzione
          </h2>
        </div>

        <div style={{ marginBottom: "var(--space-3)", fontSize: "var(--font-size-sm)" }}>
          <div className="text-muted text-xs">{fieldLabel}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginTop: 2 }}>
            <span style={{ textDecoration: "line-through", color: "var(--indaco-gray-500)" }}>{oldValue || "—"}</span>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_forward</span>
            <span className="fw-semibold">{newValue || "—"}</span>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Causa della modifica *</label>
          <select
            className="form-select"
            value={reason}
            onChange={(e) => setReason(e.target.value as CorrectionReasonOption)}
            data-testid="correction-reason-select"
          >
            {CORRECTION_REASON_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {needsFreeText && (
          <div className="form-group">
            <label className="form-label">Specifica *</label>
            <textarea
              className="form-textarea"
              rows={2}
              autoFocus
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder="Descrivi il motivo della correzione..."
              data-testid="correction-reason-freetext"
            />
          </div>
        )}

        <div className="dialog-footer">
          <button className="btn btn-outline" onClick={onCancel}>Annulla</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="correction-reason-confirm"
          >
            Conferma modifica
          </button>
        </div>
      </div>
    </div>
  );
}
